/**
 * TeamListPage — Full team list view replacing the project detail content.
 *
 * Header: Back button + "{Project Name} — Team" + member count
 * Body: List of all assigned and vacant roles.
 *
 * Future: bios, responsibilities, Impact Signature radar thumbnails.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { useApiProjectRoles } from "../../api/useApiProjectRoles";
import type { ProjectRoleSlotResponse, ProjectRoleAssignmentResponse } from "../../api/projectRoles";
import { fetchAllSecurityUsers, type SecurityUserResponse } from "../../api/security";
import { mediaSrc } from "../../api/mediaUrl";
import { canSeeIdentity, type ViewerTier } from "./projectTeamTypes";
import styles from "./TeamListPage.module.css";

interface Props {
  projectGuid: string;
  projectName: string;
  /** Viewer's permission tier for member visibility filtering. */
  viewerTier: ViewerTier;
  onBack: () => void;
}

interface TeamCard {
  slot: ProjectRoleSlotResponse;
  assignment: ProjectRoleAssignmentResponse | null;
  user: SecurityUserResponse | undefined;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2)
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (name.slice(0, 2) || "??").toUpperCase();
}

function profileImgUrl(user: SecurityUserResponse): string | null {
  return mediaSrc(user.profileImageUrl);
}

const TeamListPage: React.FC<Props> = ({ projectGuid, projectName, viewerTier, onBack }) => {
  const { t } = useAppTranslation("projectTeam");
  const { slots, loading } = useApiProjectRoles(projectGuid);

  // Fetch users
  const [allUsers, setAllUsers] = useState<SecurityUserResponse[]>([]);
  useEffect(() => {
    fetchAllSecurityUsers()
      .then((users) => setAllUsers(users.filter((u) => u.isActive)))
      .catch(() => {});
  }, []);

  const userMap = useMemo(() => {
    const map = new Map<string, SecurityUserResponse>();
    allUsers.forEach((u) => map.set(u.userGuid.toUpperCase(), u));
    return map;
  }, [allUsers]);

  // Build card list
  const cards: TeamCard[] = useMemo(() => {
    const result: TeamCard[] = [];
    const activeSlots = slots.filter(
      (s) => s.status !== "draft" && s.status !== "closed"
    );

    for (const slot of activeSlots) {
      const activeAssignments = (slot.assignments || []).filter(
        (a) => a.status === "active"
      );

      if (activeAssignments.length > 0) {
        for (const assignment of activeAssignments) {
          const user = userMap.get(assignment.userGuid.toUpperCase());
          result.push({ slot, assignment, user });
        }
      } else {
        // Show vacancy
        result.push({ slot, assignment: null, user: undefined });
      }
    }

    return result;
  }, [slots, userMap]);

  const filledCount = cards.filter((c) => c.assignment).length;

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.headerBar}>
        <button type="button" className={styles.backBtn} onClick={onBack}>
          <span className={styles.backArrow}>{"\u2190"}</span>
          {t("roster.back")}
        </button>
        <span className={styles.headerTitle}>{projectName}</span>
        <span className={styles.headerSeparator}>{"\u2014"}</span>
        <span className={styles.headerSubtitle}>{t("roster.title")}</span>
        <span className={styles.memberCount}>
          {filledCount} {t("roster.filled")}
        </span>
      </div>

      {/* Body */}
      <div className={styles.body}>
        {loading ? (
          <div className={styles.emptyState}>{t("panel.loading")}</div>
        ) : cards.length === 0 ? (
          <div className={styles.emptyState}>{t("panel.emptyText")}</div>
        ) : (
          cards.map((item, idx) => {
            const isVacant = !item.assignment;
            const redacted =
              !!item.assignment &&
              !canSeeIdentity(item.assignment.visibility, viewerTier);
            const displayName = redacted
              ? t("roster.redactedName")
              : item.user
                ? item.user.displayName || item.user.name
                : "";
            const imgUrl = redacted ? null : item.user ? profileImgUrl(item.user) : null;

            return (
              <div
                key={
                  item.assignment
                    ? item.assignment.assignmentGuid
                    : `vacant-${item.slot.roleSlotGuid}-${idx}`
                }
                className={styles.memberRow}
              >
                {/* Avatar */}
                {imgUrl ? (
                  <div className={styles.avatar}>
                    <img
                      src={imgUrl}
                      alt={displayName}
                      className={styles.avatarImg}
                    />
                  </div>
                ) : isVacant ? (
                  <div className={`${styles.avatar} ${styles.avatarVacant}`}>
                    —
                  </div>
                ) : (
                  <div className={styles.avatar}>
                    <span className={styles.avatarInitials}>
                      {redacted ? "?" : displayName ? getInitials(displayName) : "??"}
                    </span>
                  </div>
                )}

                {/* Info */}
                <div className={styles.memberInfo}>
                  {isVacant ? (
                    <div className={styles.vacantName}>{t("roster.vacant")}</div>
                  ) : (
                    <div className={styles.memberName}>{displayName}</div>
                  )}
                  <div className={styles.memberRole}>{item.slot.roleLabel}</div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default TeamListPage;
